import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { getData } from "../Firebase/firestore";
import { ALL_CATEGORY_OPTIONS, getCategoryLabel, getKindLabel, inferProductKind, PRODUCT_KINDS } from "../Data/catalog";
import { productTypeLabel } from "../Funtions/productDerive";
import "./resources.css";
import "./discovery_panel.css";

const UPLOAD_PRODUCT_ADMIN_INDEX = 7;

const PRODUCT_TYPES = ALL_CATEGORY_OPTIONS;

const getTypeLabel = (value) => (value ? getCategoryLabel(value) : "—");

const presetTypeLabel = (p) => productTypeLabel(inferProductKind(p), p.category, p.accessoryType);

const hasArr = (v) => Array.isArray(v) && v.length > 0;

// Products saved before the Discovery tags existed only rank on guessed tags in site search.
const missingTags = (p) => !hasArr(p.occasions) && !hasArr(p.festivals);

// A blank description is fine: the website writes one from the product.
const missingSeo = (p) => !p.slug || !p.canonicalUrl;

const statusOf = (p) => p.status || "published";

const formatInr = (n) => {
    const num = Number(n);
    if (Number.isNaN(num)) return String(n ?? "—");
    try {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
        }).format(num);
    } catch {
        return `\u20B9${num}`;
    }
};

function effectivePrice(price, discount) {
    const p = Number(price);
    const d = Number(discount);
    if (Number.isNaN(p)) return null;
    if (Number.isNaN(d) || d <= 0) return p;
    return Math.round(p * (1 - d / 100));
}

/** React Router history state is structured-cloned; raw preset rows can carry non-cloneable values. */
function clonePresetForRouterState(preset) {
    if (preset == null || typeof preset !== "object") return preset;
    return JSON.parse(JSON.stringify(preset));
}

function ViewProductDetailModal({ preset, onClose, onRefresh, onEdit }) {
    const [activeImg, setActiveImg] = useState(preset?.src || "");
    const [copied, setCopied] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setActiveImg(preset?.src || "");
        setConfirmAction(null);
    }, [preset?.doc, preset?.src]);

    const copyLink = async () => {
        if (!preset) return;
        const url = preset.customize_url;
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            window.prompt("Copy this link", url);
        }
    };

    if (!preset) return null;

    const gallery = [preset.src, ...(Array.isArray(preset.single) ? preset.single : [])].filter(Boolean);
    const fabricList = Array.isArray(preset.fabrics) ? preset.fabrics : [];
    const stockNum = preset.stock != null && preset.stock !== "" ? Number(preset.stock) : null;

    const handleDelete = async () => {
        setBusy(true);
        try {
            await db.collection("Presets").doc(preset.doc).delete();
            await onRefresh();
            onClose();
        } catch (e) {
            console.error(e);
        } finally {
            setBusy(false);
            setConfirmAction(null);
        }
    };

    const handleStockZero = async () => {
        setBusy(true);
        try {
            await db.collection("Presets").doc(preset.doc).set({
                ...preset,
                stock: 0,
            });
            await onRefresh();
            onClose();
        } catch (e) {
            console.error(e);
        } finally {
            setBusy(false);
            setConfirmAction(null);
        }
    };

    return (
        <div className="view-product-detail-backdrop" role="presentation" onClick={() => !busy && onClose()}>
            {confirmAction && (
                <div
                    className="view-product-confirm-backdrop view-product-confirm-backdrop--nested"
                    role="presentation"
                    onClick={() => !busy && setConfirmAction(null)}
                >
                    <div className="view-product-confirm-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <div className="view-product-confirm-head">
                            <h3>{confirmAction === "delete" ? "Delete product" : "Out of stock"}</h3>
                            <button
                                type="button"
                                className="view-product-confirm-close"
                                onClick={() => !busy && setConfirmAction(null)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <p className="view-product-confirm-body">
                            {confirmAction === "delete" ? (
                                <>
                                    Remove <strong>{preset.name || "this product"}</strong>? This cannot be undone.
                                </>
                            ) : (
                                <>Set stock to <strong>0</strong> for this product?</>
                            )}
                        </p>
                        <div className="view-product-confirm-actions">
                            <button type="button" className="view-product-confirm-cancel" disabled={busy} onClick={() => setConfirmAction(null)}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={confirmAction === "delete" ? "view-product-confirm-danger" : "view-product-confirm-primary"}
                                disabled={busy}
                                onClick={() => (confirmAction === "delete" ? handleDelete() : handleStockZero())}
                            >
                                {confirmAction === "delete" ? "Delete" : "Set stock to 0"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div
                className="view-product-detail-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="view-product-detail-title"
                onClick={(e) => e.stopPropagation()}
            >
                {busy && (
                    <div className="view-product-detail-busy" aria-busy="true">
                        <div className="edit-outfit-spinner" />
                    </div>
                )}
                <div className="view-product-detail-head">
                    <h2 id="view-product-detail-title">{preset.name?.trim() ? preset.name : "Product"}</h2>
                    <button type="button" className="view-product-detail-close" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>
                <div className="view-product-detail-body">
                    <div className="view-product-detail-media">
                        <div className="view-product-detail-hero">
                            <img src={activeImg || preset.src} alt={preset.name || ""} />
                        </div>
                        {gallery.length > 1 && (
                            <div className="view-product-detail-thumbs">
                                {gallery.map((u, i) => (
                                    <button
                                        key={`${u}-${i}`}
                                        type="button"
                                        className={`view-product-detail-thumb ${u === activeImg ? "is-active" : ""}`}
                                        onClick={() => setActiveImg(u)}
                                    >
                                        <img src={u} alt="" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="view-product-detail-info">
                        <p className="view-product-detail-name">{preset.name?.trim() ? preset.name : "—"}</p>
                        <div className="view-product-badge-row">
                            <span className={`view-product-badge ${inferProductKind(preset) === "customizer" ? "view-product-badge--custom" : ""}`}>
                                {getKindLabel(inferProductKind(preset))}
                            </span>
                            <span className="view-product-badge view-product-badge--muted">{presetTypeLabel(preset)}</span>
                            {statusOf(preset) !== "published" && (
                                <span className="view-product-badge view-product-badge--oos">{statusOf(preset)}</span>
                            )}
                            {missingTags(preset) && (
                                <span className="view-product-badge view-product-badge--muted">Needs discovery tags</span>
                            )}
                            {stockNum !== null && !Number.isNaN(stockNum) && (
                                <span className={`view-product-badge view-product-badge--stock ${stockNum === 0 ? "view-product-badge--oos" : ""}`}>
                                    Stock: {stockNum}
                                </span>
                            )}
                        </div>
                        <div className="view-product-detail-price-row">
                            <span className="view-product-price-main">{formatInr(preset.price)}</span>
                            {Number(preset.discount) > 0 && (
                                <>
                                    <span className="view-product-discount-pill">{preset.discount}% off</span>
                                    {effectivePrice(preset.price, preset.discount) != null && (
                                        <span className="view-product-price-after">
                                            {formatInr(effectivePrice(preset.price, preset.discount))}
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                        {preset.des ? (
                            <div className="view-product-detail-block">
                                <h4>Description</h4>
                                <p>{preset.des}</p>
                            </div>
                        ) : null}
                        {preset.customize_url ? (
                            <div className="view-product-detail-block">
                                <h4>Customize URL</h4>
                                <div className="view-product-detail-url-row">
                                    <a
                                        className="upload-product-import-btn"
                                        href={preset.customize_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Open builder
                                    </a>
                                    <button type="button" className="upload-product-import-clear" onClick={copyLink}>
                                        {copied ? "Copied" : "Copy link"}
                                    </button>
                                </div>
                                <p className="view-product-url-clip" title={preset.customize_url}>
                                    {preset.customize_url}
                                </p>
                            </div>
                        ) : null}
                        {fabricList.length > 0 && (
                            <div className="view-product-detail-block">
                                <h4>Fabrics ({fabricList.length})</h4>
                                <div className="upload-product-preview-items view-product-fabric-preview">
                                    {fabricList.map((row, idx) => {
                                        const piece = row.fabric || row;
                                        const label = piece.name || piece.fabric || row.type || "Fabric";
                                        const brand = piece.brand ? ` · ${piece.brand}` : "";
                                        return (
                                            <div key={idx} className="upload-product-preview-item">
                                                <div className="upload-product-preview-item-thumb">
                                                    {piece.swatch || piece.img || piece.src ? (
                                                        <img src={piece.swatch || piece.img || piece.src} alt="" />
                                                    ) : (
                                                        <div className="upload-product-preview-item-thumb-empty">
                                                            {(row.type || "?")[0]}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="upload-product-preview-item-info">
                                                    <span className="upload-product-preview-item-garment">{row.type || "—"}</span>
                                                    <span className="upload-product-preview-item-fabric">
                                                        {label}
                                                        {brand}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {(preset.washcare || preset.occasion || preset.size_fit) && (
                            <div className="view-product-detail-block">
                                <h4>Attributes</h4>
                                <div className="view-product-attrs">
                                    {preset.washcare ? (
                                        <p>
                                            <strong>Wash care</strong> {preset.washcare}
                                        </p>
                                    ) : null}
                                    {preset.occasion ? (
                                        <p>
                                            <strong>Occasion</strong> {preset.occasion}
                                        </p>
                                    ) : null}
                                    {preset.size_fit ? (
                                        <p>
                                            <strong>Size &amp; fit</strong> {preset.size_fit}
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="view-product-detail-footer view-product-compact-actions">
                    <button
                        type="button"
                        className="view-product-compact-icon view-product-compact-icon--edit"
                        title="Edit on Upload Product"
                        aria-label="Edit on Upload Product"
                        disabled={busy}
                        onClick={() => {
                            onEdit?.();
                            onClose();
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        className="view-product-compact-icon view-product-compact-icon--delete"
                        title="Delete"
                        aria-label="Delete"
                        disabled={busy}
                        onClick={() => setConfirmAction("delete")}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        className="view-product-compact-icon view-product-compact-icon--stock"
                        title="Out of stock"
                        aria-label="Mark out of stock"
                        disabled={busy}
                        onClick={() => setConfirmAction("stock")}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                            <line x1="3" y1="6" x2="21" y2="6" />
                            <path d="M16 10a4 4 0 0 1-8 0" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}

function PresetCompactCard({ preset, onRefresh, onOpenDetail, onEdit }) {
    const [confirmAction, setConfirmAction] = useState(null);
    const [busy, setBusy] = useState(false);

    const handleDelete = async () => {
        setBusy(true);
        try {
            await db.collection("Presets").doc(preset.doc).delete();
            await onRefresh();
        } catch (e) {
            console.error(e);
        } finally {
            setBusy(false);
            setConfirmAction(null);
        }
    };

    const handleStockZero = async () => {
        setBusy(true);
        try {
            await db.collection("Presets").doc(preset.doc).set({
                ...preset,
                stock: 0,
            });
            await onRefresh();
        } catch (e) {
            console.error(e);
        } finally {
            setBusy(false);
            setConfirmAction(null);
        }
    };

    const disc = Number(preset.discount);
    const showDisc = !Number.isNaN(disc) && disc > 0;

    return (
        <div className="view-product-compact-card">
            {confirmAction && (
                <div className="view-product-confirm-backdrop" role="presentation" onClick={() => !busy && setConfirmAction(null)}>
                    <div className="view-product-confirm-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <div className="view-product-confirm-head">
                            <h3>{confirmAction === "delete" ? "Delete product" : "Out of stock"}</h3>
                            <button type="button" className="view-product-confirm-close" onClick={() => !busy && setConfirmAction(null)} aria-label="Close">
                                ×
                            </button>
                        </div>
                        <p className="view-product-confirm-body">
                            {confirmAction === "delete" ? (
                                <>
                                    Remove <strong>{preset.name || "this product"}</strong>? This cannot be undone.
                                </>
                            ) : (
                                <>Set stock to <strong>0</strong> for this product?</>
                            )}
                        </p>
                        <div className="view-product-confirm-actions">
                            <button type="button" className="view-product-confirm-cancel" disabled={busy} onClick={() => setConfirmAction(null)}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={confirmAction === "delete" ? "view-product-confirm-danger" : "view-product-confirm-primary"}
                                disabled={busy}
                                onClick={() => (confirmAction === "delete" ? handleDelete() : handleStockZero())}
                            >
                                {confirmAction === "delete" ? "Delete" : "Set stock to 0"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {busy && (
                <div className="view-product-compact-loading" aria-busy="true">
                    <div className="edit-outfit-spinner" />
                </div>
            )}
            <button type="button" className="view-product-compact-main" onClick={onOpenDetail}>
                <div className="view-product-compact-image">
                    <img src={preset.src || ""} alt={preset.name || ""} />
                </div>
                <div className="view-product-compact-meta">
                    <p className="view-product-compact-name" title={preset.name || undefined}>
                        {preset.name?.trim() ? preset.name : "—"}
                    </p>
                    <p className="vp-tagline">
                        {getKindLabel(inferProductKind(preset))} · {presetTypeLabel(preset)}
                        {statusOf(preset) !== "published" && <span className="vp-flag vp-flag--status">{statusOf(preset)}</span>}
                        {missingTags(preset) && <span className="vp-flag" title="No occasions or festivals tagged — open Edit and use Suggest tags">Tags</span>}
                    </p>
                    <div className="view-product-compact-meta-row">
                        <div className="view-product-compact-price">{formatInr(preset.price)}</div>
                        {showDisc ? (
                            <div className="view-product-compact-discount">{disc}% off</div>
                        ) : (
                            <div className="view-product-compact-discount view-product-compact-discount--none">—</div>
                        )}
                    </div>
                </div>
            </button>
            <div className="view-product-compact-actions">
                <button
                    type="button"
                    className="view-product-compact-icon view-product-compact-icon--edit"
                    title="Edit on Upload Product"
                    aria-label="Edit on Upload Product"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                </button>
                <button
                    type="button"
                    className="view-product-compact-icon view-product-compact-icon--delete"
                    title="Delete"
                    aria-label="Delete"
                    onClick={(e) => {
                        e.stopPropagation();
                        setConfirmAction("delete");
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                </button>
                <button
                    type="button"
                    className="view-product-compact-icon view-product-compact-icon--stock"
                    title="Out of stock"
                    aria-label="Mark out of stock"
                    onClick={(e) => {
                        e.stopPropagation();
                        setConfirmAction("stock");
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

function presetMatchesBrand(preset, brand) {
    if (!brand) return true;
    const fabrics = Array.isArray(preset.fabrics) ? preset.fabrics : [];
    return fabrics.some((row) => {
        const piece = row.fabric || row;
        return String(piece.brand || "") === brand;
    });
}

function Presets_view() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [detailPreset, setDetailPreset] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterBrand, setFilterBrand] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    const [filterMode, setFilterMode] = useState("");
    const [openFilter, setOpenFilter] = useState(null);
    const filterBarRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (filterBarRef.current && !filterBarRef.current.contains(e.target)) {
                setOpenFilter(null);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const refresh = useCallback(async () => {
        const coll = db.collection("Presets");
        const rows = await getData(coll);
        setData(Array.isArray(rows) ? rows : []);
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const brandOptions = useMemo(() => {
        if (!data || !data.length) return [];
        const s = new Set();
        data.forEach((p) => {
            const fabrics = Array.isArray(p.fabrics) ? p.fabrics : [];
            fabrics.forEach((row) => {
                const piece = row.fabric || row;
                const b = piece.brand;
                if (b) s.add(String(b));
            });
        });
        return Array.from(s).sort((a, b) => a.localeCompare(b));
    }, [data]);

    const filtered = useMemo(() => {
        if (!data) return [];
        const q = searchQuery.trim().toLowerCase();
        return data.filter((p) => {
            if (!presetMatchesBrand(p, filterBrand)) return false;
            if (filterCategory && (p.category || "") !== filterCategory) return false;
            if (PRODUCT_KINDS.some((k) => k.value === filterMode) && inferProductKind(p) !== filterMode) return false;
            if (filterMode === "uniform" && inferProductKind(p) !== "uniform") return false;
            if (filterMode === "needs_tags" && !missingTags(p)) return false;
            if (filterMode === "needs_seo" && !missingSeo(p)) return false;
            if (filterMode === "not_live" && statusOf(p) === "published") return false;
            if (!q) return true;
            const name = String(p.name || "").toLowerCase();
            const des = String(p.des || "").toLowerCase();
            return name.includes(q) || des.includes(q);
        });
    }, [data, searchQuery, filterBrand, filterCategory, filterMode]);

    const hasActiveFilters = !!filterBrand || !!filterCategory || !!filterMode || !!searchQuery.trim();

    const needsTagsCount = data ? data.filter(missingTags).length : 0;
    const modeOptions = [
        { value: "", label: "All products" },
        ...PRODUCT_KINDS.map((k) => ({ value: k.value, label: k.label })),
        { value: "uniform", label: "Uniforms" },
        { value: "needs_tags", label: `Needs discovery tags (${needsTagsCount})` },
        { value: "needs_seo", label: "Missing SEO basics" },
        { value: "not_live", label: "Drafts and archived" },
    ];
    const modeTriggerLabel = (modeOptions.find((o) => o.value === filterMode) || modeOptions[0]).label;

    const clearFilters = () => {
        setFilterBrand("");
        setFilterCategory("");
        setFilterMode("");
        setSearchQuery("");
        setOpenFilter(null);
    };

    const goEditProduct = (preset) => {
        // Uniforms have their own form (Upload Uniform, admin index 26).
        const target = preset && preset.productKind === "uniform" ? 26 : UPLOAD_PRODUCT_ADMIN_INDEX;
        navigate(`/admin/${target}`, { state: { editPreset: clonePresetForRouterState(preset) } });
    };

    if (data === null) {
        return (
            <div className="button_upload_page button-upload-premium-page upload-product-page view-product-page-v2">
                <div className="upload-product-center view-product-center-wide">
                    <div className="view-product-state view-product-state--loading">
                        <div className="edit-outfit-spinner" aria-hidden="true" />
                        <p>Loading catalog…</p>
                    </div>
                </div>
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <div className="button_upload_page button-upload-premium-page upload-product-page view-product-page-v2">
                <div className="upload-product-center view-product-center-wide">
                    <div className="view-product-state view-product-state--empty">
                        <div className="view-product-state-icon" aria-hidden="true">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M20 7h-9M14 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h5" strokeLinecap="round" />
                                <path d="M16 16l4-4m0 0l-4-4m4 4H9" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h3>No products yet</h3>
                        <p>
                            Use <strong>Upload Product</strong> in the sidebar to add your first listing.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="button_upload_page button-upload-premium-page upload-product-page view-product-page--fixed-filter view-product-page-v2">
            {detailPreset && (
                <ViewProductDetailModal
                    preset={detailPreset}
                    onClose={() => setDetailPreset(null)}
                    onRefresh={refresh}
                    onEdit={() => goEditProduct(detailPreset)}
                />
            )}
            <div className="upload-product-center view-product-center-wide">
                <div className="view-product-filter-shell" ref={filterBarRef}>
                    <div className="view-product-filter-bar" role="search">
                        <div className="fabric-custom-select view-product-filter-dd">
                            <button
                                type="button"
                                className="fabric-custom-select-trigger view-product-filter-trigger"
                                aria-expanded={openFilter === "brand"}
                                aria-haspopup="listbox"
                                onClick={() => setOpenFilter(openFilter === "brand" ? null : "brand")}
                            >
                                <span className="view-product-filter-trigger-label">Brand</span>
                                <span className="view-product-filter-trigger-value">{filterBrand || "All brands"}</span>
                                <span className={`fabric-custom-select-arrow ${openFilter === "brand" ? "open" : ""}`}>
                                    {"\u25be"}
                                </span>
                            </button>
                            {openFilter === "brand" && (
                                <div className="fabric-custom-select-menu view-product-filter-menu" role="listbox">
                                    <button
                                        type="button"
                                        className={`fabric-custom-select-option ${!filterBrand ? "active" : ""}`}
                                        onClick={() => {
                                            setFilterBrand("");
                                            setOpenFilter(null);
                                        }}
                                    >
                                        All brands
                                    </button>
                                    {brandOptions.map((b) => (
                                        <button
                                            type="button"
                                            key={b}
                                            className={`fabric-custom-select-option ${filterBrand === b ? "active" : ""}`}
                                            onClick={() => {
                                                setFilterBrand(b);
                                                setOpenFilter(null);
                                            }}
                                        >
                                            {b}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="fabric-custom-select view-product-filter-dd">
                            <button
                                type="button"
                                className="fabric-custom-select-trigger view-product-filter-trigger"
                                aria-expanded={openFilter === "category"}
                                onClick={() => setOpenFilter(openFilter === "category" ? null : "category")}
                            >
                                <span className="view-product-filter-trigger-label">Category</span>
                                <span className="view-product-filter-trigger-value">
                                    {filterCategory ? getTypeLabel(filterCategory) : "All categories"}
                                </span>
                                <span className={`fabric-custom-select-arrow ${openFilter === "category" ? "open" : ""}`}>
                                    {"\u25be"}
                                </span>
                            </button>
                            {openFilter === "category" && (
                                <div className="fabric-custom-select-menu view-product-filter-menu" role="listbox">
                                    <button
                                        type="button"
                                        className={`fabric-custom-select-option ${!filterCategory ? "active" : ""}`}
                                        onClick={() => {
                                            setFilterCategory("");
                                            setOpenFilter(null);
                                        }}
                                    >
                                        All categories
                                    </button>
                                    {PRODUCT_TYPES.map((t) => (
                                        <button
                                            type="button"
                                            key={t.value}
                                            className={`fabric-custom-select-option ${filterCategory === t.value ? "active" : ""}`}
                                            onClick={() => {
                                                setFilterCategory(t.value);
                                                setOpenFilter(null);
                                            }}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="fabric-custom-select view-product-filter-dd">
                            <button
                                type="button"
                                className="fabric-custom-select-trigger view-product-filter-trigger"
                                aria-expanded={openFilter === "mode"}
                                onClick={() => setOpenFilter(openFilter === "mode" ? null : "mode")}
                            >
                                <span className="view-product-filter-trigger-label">Show</span>
                                <span className="view-product-filter-trigger-value">{modeTriggerLabel}</span>
                                <span className={`fabric-custom-select-arrow ${openFilter === "mode" ? "open" : ""}`}>
                                    {"\u25be"}
                                </span>
                            </button>
                            {openFilter === "mode" && (
                                <div className="fabric-custom-select-menu view-product-filter-menu" role="listbox">
                                    {modeOptions.map((opt) => (
                                        <button
                                            type="button"
                                            key={opt.value || "all"}
                                            className={`fabric-custom-select-option ${filterMode === opt.value ? "active" : ""}`}
                                            onClick={() => {
                                                setFilterMode(opt.value);
                                                setOpenFilter(null);
                                            }}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="view-product-filter-search view-product-filter-search--premium">
                            <input
                                id="view-product-search"
                                type="search"
                                className="view-product-filter-search-input"
                                placeholder="Search name or description…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoComplete="off"
                            />
                            <span className="view-product-filter-search-icon" aria-hidden="true">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" />
                                    <path d="M16.5 16.5 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </span>
                        </div>
                    </div>
                    <div className="view-product-filter-meta-row">
                        <p className="view-product-filter-meta">
                            <span className="view-product-filter-count-pill">
                                {filtered.length} / {data.length}
                            </span>
                            <span className="view-product-filter-meta-text">products shown</span>
                        </p>
                        {hasActiveFilters && (
                            <button type="button" className="view-product-clear-filters" onClick={clearFilters}>
                                Clear filters
                            </button>
                        )}
                    </div>
                </div>
                <div className="view-product-fixed-filter-spacer" aria-hidden="true" />

                {filtered.length === 0 ? (
                    <div className="view-product-state view-product-state--nomatch">
                        <h3>No matches</h3>
                        <p>Adjust filters or search, or clear everything to see the full catalog.</p>
                        <button type="button" className="view-product-clear-filters view-product-clear-filters--cta" onClick={clearFilters}>
                            Reset filters
                        </button>
                    </div>
                ) : (
                    <div className="view-product-grid view-product-grid--compact">
                        {filtered.map((preset) => (
                            <PresetCompactCard
                                key={preset.doc}
                                preset={preset}
                                onRefresh={refresh}
                                onOpenDetail={() => setDetailPreset(preset)}
                                onEdit={() => goEditProduct(preset)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Presets_view;
